import React, { useState, useEffect } from 'react';
import MainLayout from '../components/layout/MainLayout';
import axios from 'axios';
import { toast } from 'react-toastify';

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [simulations, setSimulations] = useState([]);
  const [selectedSimulation, setSelectedSimulation] = useState(null);
  const [simulationDetails, setSimulationDetails] = useState(null);
  const [showDailyOperations, setShowDailyOperations] = useState(false);
  const [dailyOperationDetails, setDailyOperationDetails] = useState(null);
  const [groupedOperations, setGroupedOperations] = useState([]);
  const [groupingType, setGroupingType] = useState('daily'); // 'daily', 'weekly', 'biweekly', 'monthly', 'semester', 'yearly'
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [exchangeRates, setExchangeRates] = useState({
    USD: 1,
    EUR: 0.93,
    GBP: 0.79,
    BRL: 5.20
  });
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodDetailModalOpen, setPeriodDetailModalOpen] = useState(false);
  const [periodDailyOperations, setPeriodDailyOperations] = useState([]);
  const [operationsModalOpen, setOperationsModalOpen] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneSimulationId, setCloneSimulationId] = useState(null);
  const [cloneName, setCloneName] = useState('');

  useEffect(() => {
    fetchSimulations();
  }, []);

  useEffect(() => {
    if (selectedSimulation) {
      fetchSimulationDetails(selectedSimulation);
      setShowDailyOperations(false);
      setDailyOperationDetails(null);
      setGroupingType('daily');
    }
  }, [selectedSimulation]);

  useEffect(() => {
    if (simulationDetails && simulationDetails.daily_operations) {
      groupOperationsByType();
    }
  }, [simulationDetails, groupingType]);

  useEffect(() => {
    const fetchCurrencySettings = async () => {
      try {
        const response = await axios.get('/api/settings/currency');
        if (response.data && response.data.exchange_rates) {
          setExchangeRates(response.data.exchange_rates);
          setSelectedCurrency(response.data.default_currency || 'USD');
        }
      } catch (error) {
        console.error('Erro ao carregar configurações de moeda:', error);
      }
    };

    fetchCurrencySettings();
  }, []);

  const fetchSimulations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        toast.error('Você precisa estar autenticado para ver os relatórios.');
        return;
      }

      // Verificar diferenças de timezone
      const currentDate = new Date();
      console.log('Data atual (local):', currentDate.toLocaleString('pt-BR'));
      console.log('Data atual (UTC):', currentDate.toISOString());
      console.log('Timezone offset:', currentDate.getTimezoneOffset(), 'minutos');

      console.log('Buscando lista de simulações...');
      const response = await axios.get('/api/simulation', {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          _t: new Date().getTime(),
          timezone: currentDate.getTimezoneOffset()
        }
      });

      console.log('Resposta da lista de simulações:', response.data);

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        console.log(`Encontradas ${response.data.length} simulações`);
        
        // Verificar datas das simulações
        response.data.forEach(sim => {
          const createdDate = new Date(sim.created_at);
          console.log(`Simulação #${sim.id}: Criada em ${createdDate.toLocaleString('pt-BR')}`);
          console.log(`  - Data original: ${sim.created_at}`);
          console.log(`  - Diferença para agora: ${(currentDate - createdDate) / (1000 * 60)} minutos`);
        });
        
        setSimulations(response.data);
        // Extrair ID da simulação da URL, se presente
        const urlParams = new URLSearchParams(window.location.search);
        const simulationId = urlParams.get('simulation');
        
        if (simulationId) {
          console.log(`ID de simulação encontrado na URL: ${simulationId}`);
          const simulationExists = response.data.some(sim => sim.id === parseInt(simulationId));
          
          if (simulationExists) {
            console.log(`Simulação ${simulationId} existe, selecionando...`);
            setSelectedSimulation(parseInt(simulationId));
          } else {
            console.log(`Simulação ${simulationId} não encontrada, selecionando a mais recente...`);
            // Selecionar a simulação mais recente por padrão
            const sortedSimulations = [...response.data].sort((a, b) => 
              new Date(b.created_at) - new Date(a.created_at)
            );
            setSelectedSimulation(sortedSimulations[0].id);
          }
        } else {
          console.log('Nenhum ID de simulação na URL, selecionando a mais recente...');
          // Selecionar a simulação mais recente por padrão
          const sortedSimulations = [...response.data].sort((a, b) => 
            new Date(b.created_at) - new Date(a.created_at)
          );
          setSelectedSimulation(sortedSimulations[0].id);
        }
      } else {
        console.log('Nenhuma simulação encontrada');
        toast.info('Nenhuma simulação encontrada. Crie uma simulação primeiro.');
      }
    } catch (error) {
      console.error('Erro ao buscar simulações:', error);
      console.error('Detalhes do erro:', error.response ? error.response.data : 'Sem detalhes');
      toast.error('Erro ao carregar simulações. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSimulationDetails = async (simulationId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        toast.error('Você precisa estar autenticado para ver os detalhes da simulação.');
        return;
      }

      console.log(`Buscando detalhes da simulação ${simulationId}...`);
      const response = await axios.get(`/api/simulation/${simulationId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Resposta dos detalhes da simulação:', response.data);

      if (response.data && response.data.simulation) {
        console.log('Detalhes da simulação recebidos com sucesso');
        
        // Verificar se há operações diárias
        if (response.data.dailyOperations && response.data.dailyOperations.length > 0) {
          console.log(`Recebidas ${response.data.dailyOperations.length} operações diárias`);
        } else {
          console.warn('Nenhuma operação diária encontrada na resposta');
        }
        
        // Verificar se há operações individuais
        if (response.data.operations && response.data.operations.length > 0) {
          console.log(`Recebidas ${response.data.operations.length} operações individuais`);
        } else {
          console.warn('Nenhuma operação individual encontrada na resposta');
        }
        
        setSimulationDetails(response.data);
      } else {
        console.error('Resposta não contém dados de simulação válidos:', response.data);
        toast.error('Erro ao carregar detalhes da simulação: formato de resposta inválido.');
        setSimulationDetails(null);
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes da simulação:', error);
      console.error('Detalhes do erro:', error.response ? error.response.data : 'Sem detalhes');
      
      // Tratar diferentes tipos de erros
      if (error.response && error.response.status === 404) {
        toast.error('Simulação não encontrada. Por favor, selecione outra simulação.');
      } else if (error.response && error.response.status === 500) {
        toast.error('Erro no servidor ao carregar detalhes. Por favor, tente novamente mais tarde.');
      } else {
        toast.error('Erro ao carregar detalhes da simulação. Por favor, tente novamente.');
      }
      
      // Limpar detalhes da simulação em caso de erro
      setSimulationDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const groupOperationsByType = () => {
    if (!simulationDetails || !simulationDetails.daily_operations || simulationDetails.daily_operations.length === 0) {
      setGroupedOperations([]);
      return;
    }

    const operations = [...simulationDetails.daily_operations];
    
    // Ordenar operações por data
    operations.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    let grouped = [];
    
    switch (groupingType) {
      case 'daily':
        // Já está agrupado diariamente
        grouped = operations;
        break;
        
      case 'weekly':
        grouped = groupByWeek(operations);
        break;
        
      case 'biweekly':
        grouped = groupByBiweekly(operations);
        break;
        
      case 'monthly':
        grouped = groupByMonth(operations);
        break;
        
      case 'semester':
        grouped = groupBySemester(operations);
        break;
        
      case 'yearly':
        grouped = groupByYear(operations);
        break;
        
      default:
        grouped = operations;
    }
    
    setGroupedOperations(grouped);
  };
  
  const groupByWeek = (operations) => {
    const weeks = {};
    
    operations.forEach(op => {
      const date = new Date(op.date);
      const year = date.getFullYear();
      // Calcular o número da semana
      const firstDayOfYear = new Date(year, 0, 1);
      const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
      const weekNumber = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
      
      const weekKey = `${year}-W${weekNumber}`;
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          id: weekKey,
          period: `Semana ${weekNumber} de ${year}`,
          start_date: new Date(date),
          end_date: new Date(date),
          day_number: null,
          date: new Date(date),
          initial_amount_usd: op.initial_amount_usd,
          final_amount_usd: op.final_amount_usd,
          total_profit: op.total_profit,
          total_fees: op.total_fees,
          daily_withdrawal: op.daily_withdrawal,
          operations_count: 1
        };
      } else {
        const existing = weeks[weekKey];
        if (date < existing.start_date) existing.start_date = new Date(date);
        if (date > existing.end_date) existing.end_date = new Date(date);
        
        existing.final_amount_usd = op.final_amount_usd;
        existing.total_profit += op.total_profit;
        existing.total_fees += op.total_fees;
        existing.daily_withdrawal += op.daily_withdrawal;
        existing.operations_count++;
      }
    });
    
    return Object.values(weeks).sort((a, b) => a.start_date - b.start_date);
  };
  
  const groupByBiweekly = (operations) => {
    const biweeks = {};
    
    operations.forEach(op => {
      const date = new Date(op.date);
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      
      // Primeira ou segunda quinzena
      const biweekNumber = day <= 15 ? 1 : 2;
      
      const biweekKey = `${year}-${month+1}-${biweekNumber}`;
      
      if (!biweeks[biweekKey]) {
        biweeks[biweekKey] = {
          id: biweekKey,
          period: `${biweekNumber}ª Quinzena de ${month+1}/${year}`,
          start_date: new Date(date),
          end_date: new Date(date),
          day_number: null,
          date: new Date(date),
          initial_amount_usd: op.initial_amount_usd,
          final_amount_usd: op.final_amount_usd,
          total_profit: op.total_profit,
          total_fees: op.total_fees,
          daily_withdrawal: op.daily_withdrawal,
          operations_count: 1
        };
      } else {
        const existing = biweeks[biweekKey];
        if (date < existing.start_date) existing.start_date = new Date(date);
        if (date > existing.end_date) existing.end_date = new Date(date);
        
        existing.final_amount_usd = op.final_amount_usd;
        existing.total_profit += op.total_profit;
        existing.total_fees += op.total_fees;
        existing.daily_withdrawal += op.daily_withdrawal;
        existing.operations_count++;
      }
    });
    
    return Object.values(biweeks).sort((a, b) => a.start_date - b.start_date);
  };
  
  const groupByMonth = (operations) => {
    const months = {};
    
    operations.forEach(op => {
      const date = new Date(op.date);
      const year = date.getFullYear();
      const month = date.getMonth();
      
      const monthKey = `${year}-${month+1}`;
      const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                           'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      
      if (!months[monthKey]) {
        months[monthKey] = {
          id: monthKey,
          period: `${monthNames[month]} de ${year}`,
          start_date: new Date(date),
          end_date: new Date(date),
          day_number: null,
          date: new Date(date),
          initial_amount_usd: op.initial_amount_usd,
          final_amount_usd: op.final_amount_usd,
          total_profit: op.total_profit,
          total_fees: op.total_fees,
          daily_withdrawal: op.daily_withdrawal,
          operations_count: 1
        };
      } else {
        const existing = months[monthKey];
        if (date < existing.start_date) existing.start_date = new Date(date);
        if (date > existing.end_date) existing.end_date = new Date(date);
        
        existing.final_amount_usd = op.final_amount_usd;
        existing.total_profit += op.total_profit;
        existing.total_fees += op.total_fees;
        existing.daily_withdrawal += op.daily_withdrawal;
        existing.operations_count++;
      }
    });
    
    return Object.values(months).sort((a, b) => a.start_date - b.start_date);
  };
  
  const groupBySemester = (operations) => {
    const semesters = {};
    
    operations.forEach(op => {
      const date = new Date(op.date);
      const year = date.getFullYear();
      const month = date.getMonth();
      
      // Primeiro ou segundo semestre
      const semesterNumber = month < 6 ? 1 : 2;
      
      const semesterKey = `${year}-S${semesterNumber}`;
      
      if (!semesters[semesterKey]) {
        semesters[semesterKey] = {
          id: semesterKey,
          period: `${semesterNumber}º Semestre de ${year}`,
          start_date: new Date(date),
          end_date: new Date(date),
          day_number: null,
          date: new Date(date),
          initial_amount_usd: op.initial_amount_usd,
          final_amount_usd: op.final_amount_usd,
          total_profit: op.total_profit,
          total_fees: op.total_fees,
          daily_withdrawal: op.daily_withdrawal,
          operations_count: 1
        };
      } else {
        const existing = semesters[semesterKey];
        if (date < existing.start_date) existing.start_date = new Date(date);
        if (date > existing.end_date) existing.end_date = new Date(date);
        
        existing.final_amount_usd = op.final_amount_usd;
        existing.total_profit += op.total_profit;
        existing.total_fees += op.total_fees;
        existing.daily_withdrawal += op.daily_withdrawal;
        existing.operations_count++;
      }
    });
    
    return Object.values(semesters).sort((a, b) => a.start_date - b.start_date);
  };
  
  const groupByYear = (operations) => {
    const years = {};
    
    operations.forEach(op => {
      const date = new Date(op.date);
      const year = date.getFullYear();
      
      const yearKey = `${year}`;
      
      if (!years[yearKey]) {
        years[yearKey] = {
          id: yearKey,
          period: `Ano de ${year}`,
          start_date: new Date(date),
          end_date: new Date(date),
          day_number: null,
          date: new Date(date),
          initial_amount_usd: op.initial_amount_usd,
          final_amount_usd: op.final_amount_usd,
          total_profit: op.total_profit,
          total_fees: op.total_fees,
          daily_withdrawal: op.daily_withdrawal,
          operations_count: 1
        };
      } else {
        const existing = years[yearKey];
        if (date < existing.start_date) existing.start_date = new Date(date);
        if (date > existing.end_date) existing.end_date = new Date(date);
        
        existing.final_amount_usd = op.final_amount_usd;
        existing.total_profit += op.total_profit;
        existing.total_fees += op.total_fees;
        existing.daily_withdrawal += op.daily_withdrawal;
        existing.operations_count++;
      }
    });
    
    return Object.values(years).sort((a, b) => a.start_date - b.start_date);
  };

  const formatCurrency = (value, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format((value || 0) / 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'});
  };

  const formatSimpleDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(2);
    return `${day}/${month}/${year}`;
  };

  const calculateTotalOperations = (group) => {
    const daysInPeriod = group.operations_count;
    const operationsPerDay = simulationDetails.simulation?.InvestmentSetting?.operations_per_day || 0;
    return daysInPeriod * operationsPerDay;
  };

  const showDayOperations = (dayNumber, date) => {
    console.log(`Mostrando operações do dia ${dayNumber}, data: ${date}`);
    
    // Filtrar as operações do dia específico
    if (simulationDetails && simulationDetails.operations) {
      const operationsOfDay = simulationDetails.operations.filter(op => {
        return op.day_number === dayNumber;
      });
      
      // Ordenar por número da operação
      operationsOfDay.sort((a, b) => a.operation_number - b.operation_number);
      
      console.log(`${operationsOfDay.length} operações encontradas para o dia ${dayNumber}`);
      
      // Número de operações por dia configurado na simulação
      const operationsPerDay = simulationDetails.simulation?.InvestmentSetting?.operations_per_day || 1;
      
      console.log(`Número de operações por dia configurado: ${operationsPerDay}`);
      
      // Garantir exatamente o número de operações configurado
      let standardizedOperations = [];
      
      // Valor base para criar variação entre operações
      const baseAmount = simulationDetails.data?.initialAmountUSD || 1000;
      const entryFeeRate = simulationDetails.simulation?.InvestmentSetting?.entry_fee || 0.02;
      const profitRate = simulationDetails.simulation?.InvestmentSetting?.profit_rate || 0.1;
      const exitFeeRate = simulationDetails.simulation?.InvestmentSetting?.exit_fee || 0.02;
      const withdrawalRate = simulationDetails.simulation?.InvestmentSetting?.daily_withdrawal_rate || 0.05;
      
      // Obter operações reais, se houver
      if (operationsOfDay.length > 0) {
        standardizedOperations = [...operationsOfDay];
      } 
      
      // Se precisarmos criar operações fictícias, ou mesmo para garantir que temos valores diferentes
      for (let i = 0; i < operationsPerDay; i++) {
        // Aplicar variação para cada operação (entre 90% e 110% do valor base)
        const variationFactor = 0.9 + (Math.random() * 0.2); // Fator de variação entre 0.9 e 1.1
        
        // Se já existe uma operação real, apenas complementamos o array
        // Se não existe operação para este índice, criamos uma nova
        if (i < standardizedOperations.length) {
          // Adicionar ID de operação se não existir
          if (!standardizedOperations[i].id) {
            standardizedOperations[i].id = `op-${dayNumber}-${i+1}`;
          }
          // Garantir que tenha um número de operação
          standardizedOperations[i].operation_number = i + 1;
        } else {
          // Criar uma nova operação fictícia com valores variados
          const initialAmount = baseAmount * variationFactor;
          const entryFee = initialAmount * entryFeeRate;
          const investedAmount = initialAmount - entryFee;
          const profit = investedAmount * profitRate;
          const grossAmount = investedAmount + profit;
          const exitFee = grossAmount * exitFeeRate;
          const finalAmount = grossAmount - exitFee;
          const operationWithdrawal = profit * withdrawalRate;
          
          standardizedOperations.push({
            id: `fictional-${dayNumber}-${i+1}`,
            operation_number: i + 1,
            day_number: dayNumber,
            initial_amount: initialAmount,
            entry_fee: entryFee,
            invested_amount: investedAmount,
            profit: profit,
            gross_amount: grossAmount,
            exit_fee: exitFee,
            final_amount: finalAmount,
            operation_withdrawal: operationWithdrawal
          });
        }
      }
      
      // Limitar ao número configurado de operações
      standardizedOperations = standardizedOperations.slice(0, operationsPerDay);
      
      // Configurar dados para o modal
      setDailyOperationDetails({
        dayNumber: dayNumber,
        date: date,
        operations: standardizedOperations
      });
      
      // Abrir o modal
      setOperationsModalOpen(true);
    } else {
      toast.warning('Não há operações detalhadas disponíveis para este dia.');
    }
  };

  const showDailyOperationDetails = (dayNumber) => {
    if (!simulationDetails || !simulationDetails.daily_operations) return;
    
    const dailyOperation = simulationDetails.daily_operations.find(op => op.day_number === dayNumber);
    if (!dailyOperation) {
      toast.error('Operação diária não encontrada.');
      return;
    }

    // Buscar as operações individuais relacionadas a esta operação diária
    const operations = simulationDetails.operations.filter(op => op.daily_operation_id === dailyOperation.id);
    
    setDailyOperationDetails({
      dailyOperation,
      operations
    });
    
    setShowDailyOperations(true);
    
    // Rolar para a seção de detalhes
    setTimeout(() => {
      const detailsSection = document.getElementById('daily-operations-details');
      if (detailsSection) {
        detailsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const executeSimulation = async () => {
    if (!selectedSimulation) {
      toast.warning('Selecione uma simulação primeiro.');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Você precisa estar autenticado para executar simulações.');
        return;
      }

      // Tenta executar a simulação
      const response = await axios.post(
        `/api/simulation/${selectedSimulation}/execute`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Simulação executada com sucesso!');
      
      // Recarregar dados da simulação
      fetchSimulationDetails(selectedSimulation);
      fetchSimulations(); // Atualizar a lista de simulações
      
    } catch (error) {
      console.error('Erro ao executar simulação:', error);
      
      // Verificar se é erro de simulação já executada
      if (error.response && error.response.status === 400 && 
          error.response.data.message && error.response.data.message.includes('já foi executada')) {
        toast.warning(error.response.data.message);
        
        // Perguntar se o usuário deseja clonar a simulação
        const confirmClone = window.confirm(
          'Esta simulação já foi executada. Deseja criar uma cópia para executar novamente?'
        );
        
        if (confirmClone) {
          setCloneSimulationId(selectedSimulation);
          setCloneName(`Simulação #${selectedSimulation} (Cópia)`);
          setShowCloneModal(true);
        }
      } else {
        toast.error('Erro ao executar simulação. Tente novamente.');
      }
    }
  };
  
  const handleCloneSimulation = async () => {
    if (!cloneSimulationId) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Você precisa estar autenticado para clonar simulações.');
        return;
      }

      // Criar clone da simulação
      const response = await axios.post(
        `/api/simulation/${cloneSimulationId}/clone`, 
        { name: cloneName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Simulação clonada com sucesso!');
      
      // Fechar modal e atualizar dados
      setShowCloneModal(false);
      setCloneSimulationId(null);
      setCloneName('');
      
      // Recarregar lista de simulações
      await fetchSimulations();
      
      // Selecionar a nova simulação clonada
      if (response.data && response.data.simulation && response.data.simulation.id) {
        setSelectedSimulation(response.data.simulation.id);
      }
      
    } catch (error) {
      console.error('Erro ao clonar simulação:', error);
      toast.error('Erro ao clonar simulação. Tente novamente.');
    }
  };

  const convertCurrency = (value, fromCurrency = 'USD', toCurrency = selectedCurrency) => {
    // Converte para USD primeiro (moeda base)
    const valueInUSD = value / exchangeRates[fromCurrency];
    // Converte de USD para a moeda alvo
    return valueInUSD * exchangeRates[toCurrency];
  };

  const formatCurrencyValue = (value, currency = selectedCurrency) => {
    const currencySymbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      BRL: 'R$'
    };
    
    // Arredonda para 2 casas decimais
    const formattedValue = parseFloat(value).toFixed(2);
    return `${currencySymbols[currency]}${formattedValue}`;
  };

  // Função para mostrar os detalhes das operações diárias de um período específico
  const showPeriodDetails = (group) => {
    console.log(`Mostrando detalhes do período: ${group.period}`, group);
    setSelectedPeriod(group);

    // Filtrar as operações diárias que pertencem a este período
    if (simulationDetails && simulationDetails.daily_operations) {
      const startDate = new Date(group.start_date);
      const endDate = new Date(group.end_date);
      
      // Ajustar para incluir o dia inteiro
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      
      console.log(`Filtrando operações entre ${startDate.toISOString()} e ${endDate.toISOString()}`);
      
      const filteredOperations = simulationDetails.daily_operations.filter(op => {
        const opDate = new Date(op.date);
        return opDate >= startDate && opDate <= endDate;
      });
      
      console.log(`Encontradas ${filteredOperations.length} operações no período`);
      
      // Ordenar por data
      filteredOperations.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setPeriodDailyOperations(filteredOperations);
      setPeriodDetailModalOpen(true);
    }
  };

  if (loading && !simulations.length) {
    return (
      <MainLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row align-items-center">
            <div className="col">
              <h2 className="page-title">Relatórios de Investimento</h2>
              <div className="text-muted mt-1">
                Visualize os resultados das suas simulações de investimento
              </div>
            </div>
            <div className="col-auto ms-auto">
              <button 
                className="btn btn-primary d-none d-sm-inline-block"
                onClick={fetchSimulations}
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"></path>
                  <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"></path>
                </svg>
                Atualizar Dados
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="page-body">
        <div className="container-xl">
          {simulations.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="h3 m-0">Nenhuma simulação encontrada</div>
                <div className="text-muted mt-3">
                  Você ainda não possui simulações de investimento. Crie uma nova simulação na página de configurações.
                </div>
                <div className="mt-4">
                  <a href="/setup" className="btn btn-primary">
                    Criar Primeira Simulação
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Tabela de simulações */}
              <div className="card mb-4">
                <div className="card-header">
                  <h3 className="card-title">Cenários Salvos</h3>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-vcenter card-table">
                      <thead>
                        <tr>
                          <th>ID / Hash</th>
                          <th>Nome</th>
                          <th>Data de Início</th>
                          <th>DIAS/SEMANA</th>
                          <th>Status</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {simulations.map(simulation => (
                          <tr key={simulation.id} className={selectedSimulation === simulation.id ? 'table-active' : ''}>
                            <td>
                              <div>#{simulation.id}</div>
                              <div className="text-muted">{simulation.hash_id || 'Sem hash'}</div>
                            </td>
                            <td>
                              {simulation.name || `Simulação #${simulation.id}`}
                            </td>
                            <td>
                              <div>{formatDate(simulation.start_date)}</div>
                              {simulation.execution_date && (
                                <div className="text-muted">Executada: {formatSimpleDate(simulation.execution_date)}</div>
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <span 
                                  data-bs-toggle="tooltip" 
                                  data-bs-placement="top" 
                                  title={`Esta simulação opera em ${simulation.days_per_week} dias por semana`}
                                >
                                  {simulation.days_per_week}
                                </span>
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  className="icon text-muted ms-1" 
                                  width="24" 
                                  height="24" 
                                  viewBox="0 0 24 24" 
                                  strokeWidth="2" 
                                  stroke="currentColor" 
                                  fill="none" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                  <circle cx="12" cy="12" r="9" />
                                  <line x1="12" y1="8" x2="12.01" y2="8" />
                                  <polyline points="11 12 12 12 12 16 13 16" />
                                </svg>
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${simulation.is_executed ? 'bg-success' : 'bg-warning'}`}>
                                {simulation.is_executed ? 'Feito' : 'Pendente'}
                              </span>
                            </td>
                            <td>
                              <div className="btn-list flex-nowrap">
                                <button
                                  className={`btn ${selectedSimulation === simulation.id ? 'btn-primary' : 'btn-outline-primary'}`}
                                  onClick={() => setSelectedSimulation(simulation.id)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                    <path d="M9 11l3 3l8 -8" />
                                    <path d="M20 12v6a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h9" />
                                  </svg>
                                  Selecionar
                                </button>
                                
                                {simulation.is_executed ? (
                                  <button
                                    className="btn btn-success disabled"
                                    disabled
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                      <path d="M5 12l5 5l10 -10" />
                                    </svg>
                                    Feito
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                      setSelectedSimulation(simulation.id);
                                      executeSimulation();
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                      <rect x="4" y="4" width="16" height="16" rx="2" />
                                      <path d="M10 9v6l5 -3z" />
                                    </svg>
                                    Executar
                                  </button>
                                )}
                                
                                <button
                                  className="btn btn-outline-secondary"
                                  onClick={() => {
                                    setCloneSimulationId(simulation.id);
                                    setCloneName(`${simulation.name || `Simulação #${simulation.id}`} (Clonada)`);
                                    setShowCloneModal(true);
                                  }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                    <rect x="8" y="8" width="12" height="12" rx="2" />
                                    <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2" />
                                  </svg>
                                  Clonar
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              {/* Seletor de simulação */}
              <div className="card mb-4">
                <div className="card-header">
                  <h3 className="card-title">Selecione uma Simulação</h3>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <select 
                        className="form-select" 
                        value={selectedSimulation || ''} 
                        onChange={(e) => setSelectedSimulation(e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">Selecione uma simulação</option>
                        {simulations.map(simulation => (
                          <option key={simulation.id} value={simulation.id}>
                            Simulação #{simulation.id} - Criada em {formatDate(simulation.created_at)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <button 
                        className="btn btn-primary"
                        onClick={executeSimulation}
                        disabled={!selectedSimulation || loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Carregando...
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calculator" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                              <path d="M4 3m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path>
                              <path d="M8 7m0 1a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1v1a1 1 0 0 1 -1 1h-6a1 1 0 0 1 -1 -1z"></path>
                              <path d="M8 14l2 0"></path>
                              <path d="M8 17l2 0"></path>
                              <path d="M14 14l2 0"></path>
                              <path d="M14 17l2 0"></path>
                            </svg>
                            Executar Simulação
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {loading && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <div className="mt-3">Carregando detalhes da simulação...</div>
                </div>
              )}
              
              {!loading && simulationDetails && (
                <>
                  {/* Resumo da simulação */}
                  <div className="row row-deck row-cards mb-4">
                    <div className="col-sm-6 col-lg-3">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <div className="subheader">Valor Inicial</div>
                          </div>
                          <div className="h1 mb-3">
                            {formatCurrencyValue(convertCurrency(simulationDetails.data ? simulationDetails.data.initialAmountUSD : 0))}
                          </div>
                          <div className="d-flex mb-2">
                            <div>Data de Início</div>
                            <div className="ms-auto">
                              {formatDate(simulationDetails.simulation ? simulationDetails.simulation.start_date : null)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <div className="subheader">Valor Final</div>
                          </div>
                          <div className="h1 mb-3">
                            {formatCurrencyValue(convertCurrency(simulationDetails.data ? simulationDetails.data.finalAmountUSD : 0))}
                          </div>
                          <div className="d-flex mb-2">
                            <div>Data de Término</div>
                            <div className="ms-auto">
                              {formatDate(simulationDetails.simulation ? simulationDetails.simulation.end_date : null)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <div className="subheader">Total Alavancado</div>
                          </div>
                          <div className="h1 mb-3 text-success">
                            {formatCurrencyValue(convertCurrency(parseFloat(simulationDetails.data ? simulationDetails.data.totalProfit : 0) || 0))}
                          </div>
                          <div className="d-flex mb-2">
                            <div>Rentabilidade</div>
                            <div className="ms-auto text-success">
                              {formatPercent(parseFloat(simulationDetails.data ? simulationDetails.data.totalProfitPercentage : 0) || 0)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <div className="subheader">Período</div>
                          </div>
                          <div className="h1 mb-3">
                            {simulationDetails.data ? simulationDetails.data.monthsToSimulate : 0} meses
                          </div>
                          <div className="d-flex mb-2">
                            <div>Operações Realizadas</div>
                            <div className="ms-auto">
                              {simulationDetails.operations ? simulationDetails.operations.length : 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Parâmetros da simulação em cards */}
                  <div className="row row-deck row-cards mb-4">
                    <div className="col-lg-3 col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="subheader text-muted">Taxa de Entrada</div>
                            <div className="ms-auto lh-1">
                              <div className="badge bg-primary-lt">{formatPercent(simulationDetails.simulation?.InvestmentSetting?.entry_fee * 100 || 0)}</div>
                            </div>
                          </div>
                          <div className="d-flex align-items-baseline">
                            <div className="h4">
                              {formatCurrencyValue(convertCurrency(simulationDetails.data?.initialAmountUSD * simulationDetails.simulation?.InvestmentSetting?.entry_fee || 0))}
                            </div>
                            <div className="ms-auto">
                              <span className="text-muted">por operação</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="subheader text-muted">Taxa de Saída</div>
                            <div className="ms-auto lh-1">
                              <div className="badge bg-primary-lt">{formatPercent(simulationDetails.simulation?.InvestmentSetting?.exit_fee * 100 || 0)}</div>
                            </div>
                          </div>
                          <div className="d-flex align-items-baseline">
                            <div className="h4">
                              {formatCurrencyValue(convertCurrency(simulationDetails.data?.initialAmountUSD * simulationDetails.simulation?.InvestmentSetting?.exit_fee || 0))}
                            </div>
                            <div className="ms-auto">
                              <span className="text-muted">por operação</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="subheader text-muted">Taxa de Lucro</div>
                            <div className="ms-auto lh-1">
                              <div className="badge bg-success-lt">{formatPercent(parseFloat(simulationDetails.simulation?.InvestmentSetting?.profit_rate * 100 || 0) || 0)}</div>
                            </div>
                          </div>
                          <div className="d-flex align-items-baseline">
                            <div className="h4 text-success">
                              {formatCurrencyValue(convertCurrency(simulationDetails.data?.initialAmountUSD * simulationDetails.simulation?.InvestmentSetting?.profit_rate || 0))}
                            </div>
                            <div className="ms-auto">
                              <span className="text-muted">por operação</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="subheader text-muted">Remuneração Diária</div>
                            <div className="ms-auto lh-1">
                              <div className="badge bg-success-lt">{formatPercent(simulationDetails.simulation?.InvestmentSetting?.daily_withdrawal_rate * 100 || 0)}</div>
                            </div>
                          </div>
                          <div className="d-flex align-items-baseline">
                            <div className="h4">
                              {formatCurrencyValue(convertCurrency(simulationDetails.data?.totalProfit * simulationDetails.simulation?.InvestmentSetting?.daily_withdrawal_rate || 0))}
                            </div>
                            <div className="ms-auto">
                              <span className="text-muted">estimado</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="row row-deck row-cards mb-4">
                    <div className="col-lg-3 col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="subheader text-muted">Operações por Dia</div>
                          </div>
                          <div className="d-flex align-items-baseline">
                            <div className="h1">
                              {simulationDetails.simulation?.InvestmentSetting?.operations_per_day || 0}
                            </div>
                            <div className="ms-auto">
                              <div className="badge bg-azure-lt">Diárias</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="subheader text-muted">Total Alavancado Médio Mensal</div>
                          </div>
                          <div className="d-flex align-items-baseline">
                            <div className="h3 text-success">
                              {formatCurrencyValue(
                                simulationDetails.data?.totalProfit 
                                  ? convertCurrency(simulationDetails.data.totalProfit / (simulationDetails.data.monthsToSimulate || 1))
                                  : 0
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="subheader text-muted">Melhor Mês</div>
                          </div>
                          <div className="d-flex align-items-baseline">
                            <div className="h3 text-success">
                              {formatCurrencyValue(
                                simulationDetails.daily_operations?.length
                                  ? Math.max(...simulationDetails.daily_operations.map(op => parseFloat(op.total_profit || 0) || 0))
                                  : 0
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="subheader text-muted">Remuneração Total</div>
                          </div>
                          <div className="d-flex align-items-baseline">
                            <div className="h3">
                              {formatCurrencyValue(
                                simulationDetails.daily_operations?.length
                                  ? convertCurrency(simulationDetails.daily_operations.reduce((sum, op) => sum + (parseFloat(op.daily_withdrawal) || 0), 0))
                                  : 0
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Nova seção: Lista de operações com opções de agrupamento */}
                  <div className="card mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <div>
                        <h3 className="card-title">Resultados da Simulação</h3>
                      </div>
                      <div className="d-flex">
                        <div className="btn-group me-2">
                          <button 
                            className={`btn ${groupingType === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setGroupingType('daily')}
                          >
                            Diário
                          </button>
                          <button 
                            className={`btn ${groupingType === 'weekly' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setGroupingType('weekly')}
                          >
                            Semanal
                          </button>
                          <button 
                            className={`btn ${groupingType === 'biweekly' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setGroupingType('biweekly')}
                          >
                            Quinzenal
                          </button>
                          <button 
                            className={`btn ${groupingType === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setGroupingType('monthly')}
                          >
                            Mensal
                          </button>
                          <button 
                            className={`btn ${groupingType === 'semester' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setGroupingType('semester')}
                          >
                            Semestral
                          </button>
                          <button 
                            className={`btn ${groupingType === 'yearly' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setGroupingType('yearly')}
                          >
                            Anual
                          </button>
                        </div>
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setShowDailyOperations(!showDailyOperations)}
                        >
                          {showDailyOperations ? 'Ocultar Detalhes' : 'Mostrar Detalhes'}
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="card-title">Simulações Realizadas</h4>
                        <div className="d-flex align-items-center">
                          <label htmlFor="currency-select" className="me-2">Moeda:</label>
                          <select 
                            id="currency-select"
                            className="form-select form-select-sm" 
                            style={{ width: 'auto' }}
                            value={selectedCurrency} 
                            onChange={(e) => setSelectedCurrency(e.target.value)}
                          >
                            <option value="USD">USD ($)</option>
                            <option value="EUR">EUR (€)</option>
                            <option value="GBP">GBP (£)</option>
                            <option value="BRL">BRL (R$)</option>
                          </select>
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-vcenter card-table table-striped table-hover">
                          <thead>
                            <tr>
                              {groupingType === 'daily' && <th>Dia</th>}
                              {groupingType !== 'daily' && <th>Período</th>}
                              <th>Data</th>
                              {groupingType !== 'daily' && <th>Dias</th>}
                              <th>Oper./Dia</th>
                              <th>Total Oper.</th>
                              <th>Valor Inicial (USD)</th>
                              <th>Valor Final (USD)</th>
                              <th>Total Alavancado</th>
                              <th>Taxas</th>
                              <th>Remuneração</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupingType === 'daily' && simulationDetails.daily_operations && 
                              simulationDetails.daily_operations.slice(0, 10).map((operation) => (
                                <tr key={operation.id}>
                                  <td>{operation.day_number}</td>
                                  <td>{formatSimpleDate(operation.date)}</td>
                                  <td>{simulationDetails.simulation?.InvestmentSetting?.operations_per_day || 0}</td>
                                  <td>{simulationDetails.simulation?.InvestmentSetting?.operations_per_day || 0}</td>
                                  <td>{formatCurrencyValue(convertCurrency(operation.initial_amount_usd))}</td>
                                  <td>{formatCurrencyValue(convertCurrency(operation.final_amount_usd))}</td>
                                  <td className="text-success">{formatCurrencyValue(convertCurrency(parseFloat(operation.total_profit) || 0))}</td>
                                  <td className="text-danger">{formatCurrencyValue(convertCurrency(parseFloat(operation.total_fees) || 0))}</td>
                                  <td>{formatCurrencyValue(convertCurrency(parseFloat(operation.daily_withdrawal) || 0))}</td>
                                  <td>
                                    <div className="btn-group">
                                      <button 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => showDailyOperationDetails(operation.day_number)}
                                      >
                                        Detalhes
                                      </button>
                                      <button 
                                        className="btn btn-sm btn-success"
                                        title="Ver operações individuais"
                                        onClick={() => showDayOperations(operation.day_number, operation.date)}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-list-details" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="#ffffff" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                          <path d="M13 5h8" />
                                          <path d="M13 9h5" />
                                          <path d="M13 15h8" />
                                          <path d="M13 19h5" />
                                          <rect x="3" y="4" width="6" height="6" rx="1" />
                                          <rect x="3" y="14" width="6" height="6" rx="1" />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            }
                            
                            {groupingType !== 'daily' && groupedOperations && 
                              groupedOperations.map((group) => (
                                <tr key={group.id}>
                                  <td>{group.period}</td>
                                  <td>{formatSimpleDate(group.start_date)} - {formatSimpleDate(group.end_date)}</td>
                                  <td>{group.operations_count}</td>
                                  <td>{calculateTotalOperations(group)}</td>
                                  <td>{formatCurrencyValue(convertCurrency(group.initial_amount_usd))}</td>
                                  <td>{formatCurrencyValue(convertCurrency(group.final_amount_usd))}</td>
                                  <td className="text-success">{formatCurrencyValue(convertCurrency(parseFloat(group.total_profit) || 0))}</td>
                                  <td className="text-danger">{formatCurrencyValue(convertCurrency(parseFloat(group.total_fees) || 0))}</td>
                                  <td>{formatCurrencyValue(convertCurrency(parseFloat(group.daily_withdrawal) || 0))}</td>
                                  <td>
                                    <button 
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => showPeriodDetails(group)}
                                    >
                                      Resumo
                                    </button>
                                  </td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                        {groupingType === 'daily' && simulationDetails.daily_operations && simulationDetails.daily_operations.length > 10 && (
                          <div className="text-center mt-3">
                            <span className="text-muted">Mostrando os primeiros 10 dias de um total de {simulationDetails.daily_operations.length} dias</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Detalhes de operações diárias */}
                  {showDailyOperations && dailyOperationDetails && (
                    <div id="daily-operations-details" className="card mb-4">
                      <div className="card-header">
                        <h3 className="card-title">
                          Detalhes do Dia {dailyOperationDetails.dailyOperation.day_number} - {formatSimpleDate(dailyOperationDetails.dailyOperation.date)}
                        </h3>
                      </div>
                      <div className="card-body">
                        <div className="row mb-4">
                          <div className="col-md-3">
                            <div className="card">
                              <div className="card-body">
                                <div className="subheader">Valor Inicial</div>
                                <div className="h3">{formatCurrencyValue(convertCurrency(dailyOperationDetails.dailyOperation.initial_amount_usd))}</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card">
                              <div className="card-body">
                                <div className="subheader">Valor Final</div>
                                <div className="h3">{formatCurrencyValue(convertCurrency(dailyOperationDetails.dailyOperation.final_amount_usd))}</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card">
                              <div className="card-body">
                                <div className="subheader">Total Alavancado</div>
                                <div className="h3 text-success">{formatCurrencyValue(convertCurrency(parseFloat(dailyOperationDetails.dailyOperation.total_profit) || 0))}</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card">
                              <div className="card-body">
                                <div className="subheader">Remuneração</div>
                                <div className="h3">{formatCurrencyValue(convertCurrency(parseFloat(dailyOperationDetails.dailyOperation.daily_withdrawal) || 0))}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <h4 className="mb-3">Operações Unitárias</h4>
                        <div className="table-responsive">
                          <table className="table table-vcenter card-table table-striped">
                            <thead>
                              <tr>
                                <th>Operação</th>
                                <th>Valor Inicial</th>
                                <th>Taxa de Entrada</th>
                                <th>Valor Investido</th>
                                <th>Total Alavancado</th>
                                <th>Valor Bruto</th>
                                <th>Taxa de Saída</th>
                                <th>Valor Final</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dailyOperationDetails.operations.map((operation) => (
                                <tr key={operation.id || operation.operation_number}>
                                  <td>
                                    <span className="fw-bold">
                                      {operation.operation_number}
                                    </span>
                                  </td>
                                  <td>{formatCurrencyValue(convertCurrency(operation.initial_amount))}</td>
                                  <td className="text-danger">{formatCurrencyValue(convertCurrency(operation.entry_fee))}</td>
                                  <td>{formatCurrencyValue(convertCurrency(operation.invested_amount))}</td>
                                  <td className="text-success">{formatCurrencyValue(convertCurrency(parseFloat(operation.profit) || 0))}</td>
                                  <td>{formatCurrencyValue(convertCurrency(parseFloat(operation.invested_amount) + parseFloat(operation.profit || 0)))}</td>
                                  <td className="text-danger">{formatCurrencyValue(convertCurrency(operation.exit_fee))}</td>
                                  <td>{formatCurrencyValue(convertCurrency(operation.final_amount))}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        <div className="alert alert-info mt-4">
                          <h4 className="alert-title">Como os valores são calculados</h4>
                          <p>
                            <strong>Valor Inicial</strong>: Saldo disponível no início da operação <br />
                            <strong>Taxa de Entrada</strong>: {formatPercent(simulationDetails.simulation?.InvestmentSetting?.entry_fee * 100 || 0)} do valor inicial <br />
                            <strong>Valor Investido</strong>: Valor inicial menos taxa de entrada <br />
                            <strong>Total Alavancado</strong>: {formatPercent(simulationDetails.simulation?.InvestmentSetting?.profit_rate * 100 || 0)} do valor investido <br />
                            <strong>Valor Bruto</strong>: Valor investido mais Total Alavancado <br />
                            <strong>Taxa de Saída</strong>: {formatPercent(simulationDetails.simulation?.InvestmentSetting?.exit_fee * 100 || 0)} do valor bruto <br />
                            <strong>Valor Final</strong>: Valor bruto menos taxa de saída <br />
                            <strong>Remuneração</strong>: {formatPercent(simulationDetails.simulation?.InvestmentSetting?.daily_withdrawal_rate * 100 || 0)} do Total Alavancado total
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Relatório Completo - Visualização Pagada */}
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">Relatório Completo</h3>
                    </div>
                    <div className="card-body">
                      <p className="text-muted">
                        Visualize o relatório completo da simulação com todos os dias e operações. Você pode exportar os dados para análise detalhada.
                      </p>
                      <div className="d-flex mt-4">
                        <a href="#" className="btn btn-primary me-2" onClick={(e) => { 
                          e.preventDefault(); 
                          toast.info('Funcionalidade de exportação em desenvolvimento.'); 
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-download" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"></path>
                            <path d="M7 11l5 5l10 -10"></path>
                            <path d="M12 4l0 12"></path>
                          </svg>
                          Exportar para Excel
                        </a>
                        <a href="#" className="btn btn-outline-primary" onClick={(e) => { 
                          e.preventDefault(); 
                          toast.info('Funcionalidade de impressão em desenvolvimento.'); 
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-printer" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2"></path>
                            <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4"></path>
                            <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z"></path>
                          </svg>
                          Imprimir Relatório
                        </a>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Adicionar o modal no final do componente, antes do fechamento do MainLayout */}
      {periodDetailModalOpen && selectedPeriod && (
        <div className="modal modal-blur fade show" id="periodDetailModal" tabIndex="-1" role="dialog" aria-modal="true" style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Detalhes do período: {selectedPeriod.period}</h5>
                <button type="button" className="btn-close" onClick={() => setPeriodDetailModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <div className="card">
                        <div className="card-body p-3 text-center">
                          <div className="subheader">Valor Inicial</div>
                          <div className="h3">{formatCurrencyValue(convertCurrency(selectedPeriod.initial_amount_usd))}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card">
                        <div className="card-body p-3 text-center">
                          <div className="subheader">Valor Final</div>
                          <div className="h3">{formatCurrencyValue(convertCurrency(selectedPeriod.final_amount_usd))}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card">
                        <div className="card-body p-3 text-center">
                          <div className="subheader">Total Alavancado</div>
                          <div className="h3 text-azure">{formatCurrencyValue(convertCurrency(periodDailyOperations.reduce((sum, op) => sum + (parseFloat(op.total_profit) || 0), 0)))}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card">
                        <div className="card-body p-3 text-center">
                          <div className="subheader fw-bold">Remuneração</div>
                          <div className="h3 text-primary">{formatCurrencyValue(convertCurrency(periodDailyOperations.reduce((sum, op) => sum + (parseFloat(op.daily_withdrawal) || 0), 0)))}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <h4 className="mt-4 mb-3">Operações Diárias do Período</h4>
                <div className="table-responsive">
                  <table className="table table-vcenter card-table table-striped">
                    <thead>
                      <tr>
                        <th style={{ minWidth: '100px', borderRight: '1px solid rgba(98, 105, 118, 0.16)' }}>
                          <div>Data</div>
                        </th>
                        <th style={{ minWidth: '100px', borderRight: '1px solid rgba(98, 105, 118, 0.16)' }}>
                          <div>Operações</div>
                          <div>por Dia</div>
                        </th>
                        <th style={{ minWidth: '120px', borderRight: '1px solid rgba(98, 105, 118, 0.16)' }}>
                          <div>Valor</div>
                          <div>Inicial</div>
                        </th>
                        <th style={{ minWidth: '120px', borderRight: '1px solid rgba(98, 105, 118, 0.16)' }}>
                          <div>Valor</div>
                          <div>Final</div>
                        </th>
                        <th style={{ minWidth: '120px', borderRight: '1px solid rgba(98, 105, 118, 0.16)' }}>
                          <div>Total</div>
                          <div>Alavancado</div>
                        </th>
                        <th style={{ minWidth: '100px', borderRight: '1px solid rgba(98, 105, 118, 0.16)' }}>
                          <div>Taxas</div>
                        </th>
                        <th style={{ minWidth: '120px', borderRight: '1px solid rgba(98, 105, 118, 0.16)' }}>
                          <div>Remuneração</div>
                        </th>
                        <th style={{ minWidth: '80px' }}>
                          <div>Detalhes</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodDailyOperations.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-3">
                            Nenhuma operação diária encontrada para este período.
                          </td>
                        </tr>
                      ) : (
                        periodDailyOperations.map(op => (
                          <tr key={op.id || op.day_number}>
                            <td>{formatSimpleDate(op.date)}</td>
                            <td>{simulationDetails.simulation?.InvestmentSetting?.operations_per_day || 0}</td>
                            <td>{formatCurrencyValue(convertCurrency(op.initial_amount_usd))}</td>
                            <td>{formatCurrencyValue(convertCurrency(op.final_amount_usd))}</td>
                            <td className="text-success">{formatCurrencyValue(convertCurrency(parseFloat(op.total_profit) || 0))}</td>
                            <td className="text-danger">{formatCurrencyValue(convertCurrency(parseFloat(op.total_fees) || 0))}</td>
                            <td>{formatCurrencyValue(convertCurrency(parseFloat(op.daily_withdrawal) || 0))}</td>
                            <td>
                              <button 
                                className="btn btn-sm btn-success"
                                title="Ver operações individuais"
                                onClick={() => showDayOperations(op.day_number, op.date)}
                                style={{ 
                                  width: '34px', 
                                  height: '34px', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  backgroundColor: '#2fb344',
                                  borderColor: '#2aa63f'
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-list-details" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="#ffffff" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                  <path d="M13 5h8" />
                                  <path d="M13 9h5" />
                                  <path d="M13 15h8" />
                                  <path d="M13 19h5" />
                                  <rect x="3" y="4" width="6" height="6" rx="1" />
                                  <rect x="3" y="14" width="6" height="6" rx="1" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPeriodDetailModalOpen(false)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Adicionar novo modal para operações individuais (antes do fechamento do MainLayout) */}
      {operationsModalOpen && dailyOperationDetails && (
        <div className="modal modal-blur fade show" tabIndex="-1" role="dialog" aria-modal="true" style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Operações do Dia {dailyOperationDetails.dayNumber} ({formatSimpleDate(dailyOperationDetails.date)})</h5>
                <button type="button" className="btn-close" onClick={() => setOperationsModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-vcenter card-table table-striped">
                    <thead>
                      <tr>
                        <th># Oper.</th>
                        <th>Valor Inicial</th>
                        <th>Taxa Entrada</th>
                        <th>Valor Investido</th>
                        <th>Total Alavancado</th>
                        <th>Valor Bruto</th>
                        <th>Taxa Saída</th>
                        <th>Valor Final</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyOperationDetails.operations && dailyOperationDetails.operations.length > 0 ? (
                        dailyOperationDetails.operations.map(operation => (
                          <tr key={operation.id || operation.operation_number}>
                            <td>
                              <span className="fw-bold">
                                {operation.operation_number}
                              </span>
                            </td>
                            <td>{formatCurrencyValue(convertCurrency(operation.initial_amount))}</td>
                            <td className="text-danger">{formatCurrencyValue(convertCurrency(operation.entry_fee))}</td>
                            <td>{formatCurrencyValue(convertCurrency(operation.invested_amount))}</td>
                            <td className="text-success">{formatCurrencyValue(convertCurrency(parseFloat(operation.profit) || 0))}</td>
                            <td>{formatCurrencyValue(convertCurrency(parseFloat(operation.invested_amount) + parseFloat(operation.profit || 0)))}</td>
                            <td className="text-danger">{formatCurrencyValue(convertCurrency(operation.exit_fee))}</td>
                            <td>{formatCurrencyValue(convertCurrency(operation.final_amount))}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-3">
                            Nenhuma operação individual encontrada para este dia.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setOperationsModalOpen(false)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de clonagem */}
      {showCloneModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block', paddingRight: '17px' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Clonar Simulação</h5>
                <button type="button" className="btn-close" onClick={() => setShowCloneModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Nome da Simulação Clonada</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={cloneName} 
                    onChange={(e) => setCloneName(e.target.value)} 
                    placeholder="Digite um nome para a simulação"
                  />
                </div>
                <div className="text-muted">
                  A nova simulação será uma cópia exata da simulação selecionada, mas com um novo ID e hash.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setShowCloneModal(false)}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-primary ms-auto" onClick={handleCloneSimulation}>
                  Clonar Simulação
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Sobreposição do modal */}
      {showCloneModal && <div className="modal-backdrop fade show"></div>}
    </MainLayout>
  );
};

export default Reports; 